'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { getLiveWorldCupFixtures, getUpcomingFixtures, isInPlay, type LiveFixture } from '@/lib/api-football';
import { useLanguage } from '@/contexts/LanguageContext';

interface Stage {
  id: string;
  label: string;
  count: number;
}

export default function TournamentStageBar() {
  const [active, setActive] = useState('stage-all');
  const { t } = useLanguage();
  const [stages, setStages] = useState<Stage[]>([
    { id: 'stage-all', label: 'All Matches', count: 0 },
    { id: 'stage-live', label: 'Live Now', count: 0 },
    { id: 'stage-upcoming', label: 'Upcoming', count: 0 },
  ]);

  const buildStages = useCallback(async () => {
    try {
      const [liveData, upcomingData] = await Promise.all([
        getLiveWorldCupFixtures(),
        getUpcomingFixtures(20),
      ]);

      const live = liveData ?? [];
      const upcoming = upcomingData ?? [];
      const liveCount = live.filter(m => isInPlay(m.fixture.status) || m.fixture.status.short === 'HT').length;
      const upcomingCount = upcoming.filter(m => m.fixture.status.short === 'NS').length;
      const total = live.length + upcoming.length;

      // Build round-based stages from live fixtures
      const roundMap: Record<string, number> = {};
      [...live, ...upcoming].forEach(m => {
        const round = m.league.round;
        if (round) roundMap[round] = (roundMap[round] ?? 0) + 1;
      });

      const roundStages: Stage[] = Object.entries(roundMap).map(([round, count]) => ({
        id: `stage-${round.toLowerCase().replace(/\s+/g, '-')}`,
        label: round,
        count,
      }));

      setStages([
        { id: 'stage-all', label: t.dashboard.allMatches, count: total },
        { id: 'stage-live', label: t.dashboard.liveNow, count: liveCount },
        { id: 'stage-upcoming', label: t.dashboard.upcoming, count: upcomingCount },
        ...roundStages,
      ]);
    } catch {
      setStages([
        { id: 'stage-all', label: t.dashboard.allMatches, count: 0 },
        { id: 'stage-live', label: t.dashboard.liveNow, count: 0 },
        { id: 'stage-upcoming', label: t.dashboard.upcoming, count: 0 },
      ]);
    }
  }, [t]);

  useEffect(() => {
    buildStages();
    const interval = setInterval(buildStages, 30_000);
    return () => clearInterval(interval);
  }, [buildStages]);

  return (
    <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin pb-1">
      {stages.map((stage) => (
        <button
          key={stage.id}
          onClick={() => setActive(stage.id)}
          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${
            active === stage.id
              ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/8'
          }`}
          style={active !== stage.id ? { background: 'hsl(222 35% 13%)' } : {}}
        >
          {stage.label}
          <span
            className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
              active === stage.id ? 'bg-white/20 text-white' : 'text-slate-500'
            }`}
            style={active !== stage.id ? { background: 'hsl(222 25% 18%)' } : {}}
          >
            {stage.count}
          </span>
        </button>
      ))}
    </div>
  );
}